import { type Locale, defaultLocale, isLocale } from "./locales";

const LOCALE_COOKIE = "locale";

function parseCookie(cookieHeader: string | null): Record<string, string> {
	if (!cookieHeader) return {};

	return Object.fromEntries(
		cookieHeader.split(";").map(part => {
			const [key, ...rest] = part.trim().split("=");

			return [key, decodeURIComponent(rest.join("="))];
		}),
	);
}

type LanguagePreference = {
	locale: Locale;
	q: number;
	order: number;
};

function detectFromAcceptLanguage(request: Request): Locale | null {
	const header = request.headers.get("Accept-Language");

	if (!header) return null;

	const preferences = header
		.split(",")
		.map<LanguagePreference | null>((part, order) => {
			const [rawTag, ...params] = part.trim().split(";");
			const tag = rawTag?.toLowerCase();

			if (!tag) return null;

			const qParam = params.find(param => param.trim().startsWith("q="));
			const q = qParam ? Number(qParam.trim().slice(2)) : 1;

			if (Number.isNaN(q) || q <= 0) return null;

			if (tag.startsWith("ja")) return { locale: "ja", q, order };
			if (tag.startsWith("en")) return { locale: "en", q, order };

			return null;
		})
		.filter((preference): preference is LanguagePreference => Boolean(preference))
		.sort((a, b) => b.q - a.q || a.order - b.order);

	return preferences[0]?.locale ?? null;
}

/** @deprecated Legacy /ja prefix URLs only — kept for redirects */

export function getLocaleFromPathname(pathname: string): Locale | null {
	const match = pathname.match(/^\/(ja|en)(\/|$)/);

	const value = match?.[1];

	return value && isLocale(value) ? value : null;
}

export function getLocaleFromRequest(request: Request): Locale {
	const cookieLocale = parseCookie(request.headers.get("Cookie"))[LOCALE_COOKIE];

	if (isLocale(cookieLocale)) {
		return cookieLocale;
	}

	return detectFromAcceptLanguage(request) ?? defaultLocale;
}

export function localeCookieHeader(locale: Locale): string {
	return `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`;
}

export function stripLocalePrefix(pathname: string): string {
	const match = pathname.match(/^\/(ja|en)(\/.*|$)/);

	if (!match) return pathname;

	return match[2] || "/";
}

export function localizedPath(path: string, _locale?: Locale): string {
	if (path.startsWith("http") || path.startsWith("#")) {
		return path;
	}

	const normalized = path.startsWith("/") ? path : `/${path}`;

	return stripLocalePrefix(normalized);
}

export function switchLocalePath(pathname: string, search: string, _locale: Locale): string {
	const stripped = stripLocalePrefix(pathname);

	return `${stripped || "/"}${search}`;
}

export function legacyLocaleRedirectPath(pathname: string): string {
	return stripLocalePrefix(pathname) || "/";
}
